import { useMemo } from 'react';
import { Paper, Text } from '@mantine/core';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable';
import { TransactionDetailPanel } from '@dealstack/frontend/app/pages/Project/Cap/BatchAnalysis/components/TransactionDetailPanel';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { BatchDocumentTransactions } from '@dealstack/frontend/app/services/DocumentExtraction.service';
import { getColumns, toRow, type TransactionRow } from './BatchTransactionList.utils';

interface Props {
	doc: BatchDocumentTransactions;
	onMerge?: (transactionId: string) => void;
	onDiscard?: (transactionId: string) => void;
	mergedTransactionIds?: Set<string>;
}

export const DocumentTransactionTable = ({
	doc,
	onMerge,
	onDiscard,
	mergedTransactionIds,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const columns = useMemo(() => getColumns(tCapManage), [tCapManage]);
	const rows = useMemo(() => doc.transactions.map((tx) => toRow(tx, doc)), [doc]);

	if (doc.transactions.length === 0) {
		return (
			<Paper withBorder p="sm">
				<Text size="sm" fw={600} mb="xs">
					{doc.fileName}
				</Text>
				<Text size="xs" color="#4F616C">
					{tCapManage('transactions.add_from_document.no_transactions_extracted')}
				</Text>
			</Paper>
		);
	}

	return (
		<Paper withBorder p="sm">
			<Text size="sm" fw={600} mb="xs">
				{doc.fileName}
			</Text>
			<BasicMantineTable<TransactionRow>
				columns={columns}
				data={rows}
				hideTopToolbar
				enableExpanding
				enableColumnResizing={true}
				mantineTableBodyRowProps={({ row }) => ({
					onClick: () => row.toggleExpanded(),
					style: { cursor: 'pointer' },
				})}
				renderDetailPanel={({ row }) => {
					const txId = row.original.transactionId;
					const isMerged = mergedTransactionIds?.has(txId) ?? false;
					return (
						<TransactionDetailPanel
							transaction={row.original.enrichedTransaction}
							onMerge={onMerge && !isMerged ? () => onMerge(txId) : undefined}
							onDiscard={onDiscard && !isMerged ? () => onDiscard(txId) : undefined}
							isMerged={isMerged}
						/>
					);
				}}
				mantineDetailPanelProps={{
					style: {
						backgroundColor: '#f8f9fa',
						paddingTop: '0',
						paddingBottom: '0',
					},
				}}
			/>
		</Paper>
	);
};
